import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getToken, logErrors, urlConfig } from "../../api/getUser";
import { auth } from "../../api/firebase";

const initialState = {
  list: [],
  templates: [],
  allServiceTaskCategories: [],
  status: "idle",
  templatesStatus: "idle",
  error: null,
};

export const fetchServicesTasks = createAsyncThunk(
  "servicesTasks/fetchServicesTasks",
  async (apiData) => {
    try {
      const token = await getToken(); //sessionStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      const email = auth?.currentUser?.email; //sessionStorage.getItem("email");
      const response = await axios.post(
        urlConfig.commonNew,
        { ...apiData, email },
        config,
      );
      const data = response?.data[0]?.value?.user_data;
      if (!response?.data[0]?.value?.success) {
        const errLogData = {
          url: urlConfig.commonNew,
          method_type: "POST",
          error: JSON.stringify(response.data),
          request: JSON.stringify({ ...apiData, email }),
          placeholder: "",
        };
        await logErrors(errLogData);
      }
      return data;
    } catch (err) {
      const errLogData = {
        url: urlConfig.commonNew,
        method_type: "POST",
        error: err?.message,
        request: err?.config?.data,
        placeholder: "",
      };
      await logErrors(errLogData);
      return err.message;
    }
  },
);

export const fetchServiceTaskCategories = createAsyncThunk(
  "servicesTasks/fetchServiceTaskCategories",
  async ({ force } = {}, { getState }) => {
    const state = getState();
    const existing = state.servicesTasks?.allServiceTaskCategories || [];
    if (!force && Array.isArray(existing) && existing.length > 0) {
      return { categories: existing, fromCache: true };
    }
    try {
      const token = await getToken();
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      const email = auth?.currentUser?.email;
      const response = await axios.post(
        urlConfig.workOrder,
        { querystring: "fetch_all_service_task_category", email },
        config,
      );
      const raw = response?.data[0]?.value?.service_categories;
      const categories = Array.isArray(raw)
        ? raw.map((c) => ({ label: c.category_name, value: c.st_category_id
 }))
        : [];
      return { categories };
    } catch (err) {
      const errLogData = {
        url: urlConfig.commonNew,
        method_type: "POST",
        error: err?.message,
        request: err?.config?.data,
        placeholder: "",
      };
      await logErrors(errLogData);
      return { categories: [] };
    }
  },
);

export const fetchServiceTaskTemplates = createAsyncThunk(
  "servicesTasks/fetchServiceTaskTemplates",
  async (apiData) => {
    try {
      const token = await getToken();
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      // Reuse auth.currentUser.email logic from above if needed, but apiData passed usually contains needed filters
      // Ensure email is attached if the API requires it
      const email = auth?.currentUser?.email;

      const response = await axios.post(
        urlConfig.workOrder, // Assuming templates are under workorder endpoint based on addEdit usage
        { ...apiData, email },
        config,
      );

      // Adjust response path based on actual API structure.
      // Existing fetch uses response?.data[0]?.value?.user_data
      // We will assume similar structure or standard response
      const data = response?.data;

      if (!data?.[0]?.value?.success) {
        // handle error logging
        const errLogData = {
          url: urlConfig.workOrder,
          method_type: "POST",
          error: JSON.stringify(response.data),
          request: JSON.stringify({ ...apiData, email }),
          placeholder: "",
        };
        await logErrors(errLogData);
      }

      // Return the actual list. Adjust this map if necessary based on API response
      // Assuming it returns a list of templates
      return data?.[0]?.value?.service_templates || [];
    } catch (err) {
      const errLogData = {
        url: urlConfig.workOrder,
        method_type: "POST",
        error: err?.message,
        request: err?.config?.data,
        placeholder: "",
      };
      await logErrors(errLogData);
      return [];
    }
  },
);

const serviceTasksSlice = createSlice({
  name: "servicesTasks",
  initialState,
  // reducers: {
  //   setAllServiceTaskCategories(state, action) {
  //     state.allServiceTaskCategories = Array.isArray(action.payload)
  //       ? action.payload
  //       : [];
  //   },
  // },
  extraReducers(builder) {
    builder
      .addCase(fetchServiceTaskCategories.fulfilled, (state, action) => {
        const { categories } = action.payload || {};
        if (Array.isArray(categories)) {
          state.allServiceTaskCategories = categories;
        }
      })
      .addCase(fetchServicesTasks.pending, (state, action) => {
        state.status = "loading";
      })
      .addCase(fetchServicesTasks.fulfilled, (state, action) => {
        state.status = "succeeded";
        const serviceTasksList = [];
        action.payload.forEach((item) => {
          serviceTasksList.push({
            ...item,
            value: item.service_task_id,
            label: item.service_task_name,
            laborCost: "",
            partsCost: "",
            totalCost: "",
          });
        });
        //console.log('action.payload', action.payload);
        state.list = serviceTasksList; //action.payload;
      })
      .addCase(fetchServicesTasks.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
        state.error = action.error.message;
      })
      .addCase(fetchServiceTaskTemplates.pending, (state) => {
        state.templatesStatus = "loading";
      })
      .addCase(fetchServiceTaskTemplates.fulfilled, (state, action) => {
        state.templatesStatus = "succeeded";
        // Map response to dropdown options if needed, or keep raw
        // Let's assume we store them as { value, label, taskIds }
        // API likely returns { template_id, template_name, template_task_ids }
        const templates = action.payload.map((t) => ({
          value: t.template_id,
          label: t.template_name,
          taskIds: t.template_task_ids,
          ...t, // keep original data
        }));
        state.templates = templates;
      })
      .addCase(fetchServiceTaskTemplates.rejected, (state, action) => {
        state.templatesStatus = "failed";
      });
  },
});

//export const { setAllServiceTaskCategories } = serviceTasksSlice.actions;
export default serviceTasksSlice.reducer;
